import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  Clock,
  Lock,
  type LucideIcon,
  RefreshCw,
  RotateCcw,
  TimerOff,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';

// The sessions list shows a run's state on TWO axes, and they mean different things:
//
//   Original Status  — what OUR SYSTEM concluded (risk rules, step outcomes, locks,
//                      the validity window). Read-only here; nothing an admin clicks
//                      can change it, because it is the record of what happened.
//   Verified Status  — what the TENANT ADMIN decided. Editable, and independent: a
//                      run can read Rejected on the system axis and Approved on this
//                      one. That pair is the audit trail, not a contradiction.
//
// Colours are the designer's, including the 0D-suffix (≈5%) tinted fill. Icons are
// lucide rather than the design's bespoke SVGs — same semantics, and it keeps the
// icon set consistent with the rest of the app instead of adding 12 one-use assets.

interface AxisMeta {
  label: string;
  color: string; // border + text + icon
  fill: string; // tinted background
  Icon: LucideIcon;
}

// Grey used for both "nothing has happened yet" states, on either axis.
const NEUTRAL = { color: '#B3B3B3', fill: '#F9F9F9' } as const;

const ORIGINAL: Record<string, AxisMeta> = {
  NotStarted: { label: 'Not Started', ...NEUTRAL, Icon: CircleDashed },
  // Static, like the design's Status-In-Progress.svg — a two-arc circular arrow with
  // arrowheads, which is what RefreshCw draws. It does NOT spin: the design animates
  // nothing in this table, and a perpetual spinner in a list row reads as "this row is
  // loading right now", when it actually means "the applicant hasn't finished yet" —
  // a state that can sit unchanged for days.
  InProgress: { label: 'In Progress', color: '#0048FF', fill: '#0048FF0D', Icon: RefreshCw },
  Locked: { label: 'Locked', color: '#9747FF', fill: '#9747FF0D', Icon: Lock },
  Expired: { label: 'Expired', color: '#FF383C', fill: '#FF383C0D', Icon: TimerOff },
  InReview: { label: 'In Review', color: '#F27310', fill: '#DA74000D', Icon: Clock },
  Approved: { label: 'Approved', color: '#00C232', fill: '#00C2320D', Icon: CheckCircle2 },
  Rejected: { label: 'Rejected', color: '#BF3636', fill: '#BF36360D', Icon: XCircle },
};

const VERIFIED: Record<string, AxisMeta> = {
  NotApplicable: { label: 'NA', ...NEUTRAL, Icon: CircleDashed },
  Approved: { label: 'Approved', color: '#00C232', fill: '#00C2320D', Icon: CheckCircle2 },
  Rejected: { label: 'Rejected', color: '#BF3636', fill: '#BF36360D', Icon: Ban },
  ResubmitRequested: {
    label: 'Resubmit Requested',
    color: '#0048FF',
    fill: '#0048FF0D',
    Icon: RotateCcw,
  },
  ExceptionRequested: {
    label: 'Exception Requested',
    color: '#FF383C',
    fill: '#FF383C0D',
    Icon: AlertTriangle,
  },
};

/**
 * The admin-settable verdicts, in the order the dropdown offers them.
 * `NotApplicable` leads because it is how a decision is undone.
 */
export const VERIFIED_STATUS_ORDER = [
  'NotApplicable',
  'Approved',
  'Rejected',
  'ResubmitRequested',
  'ExceptionRequested',
] as const;

export type VerifiedStatusValue = (typeof VERIFIED_STATUS_ORDER)[number];

export function verifiedStatusLabel(value: string): string {
  return VERIFIED[value]?.label ?? value;
}

/**
 * Falls back to the raw value rather than to a guessed status: if the backend ever
 * grows an eighth value, showing its name is honest, whereas defaulting it to
 * "In Progress" would quietly misreport the run.
 */
export function originalMeta(status: string): AxisMeta {
  return ORIGINAL[status] ?? { label: status || 'Unknown', ...NEUTRAL, Icon: CircleDashed };
}

/**
 * An unknown verdict falls back to the neutral "NA" styling but keeps the raw value
 * as its label — the same honesty as {@link originalMeta}, and it also covers the
 * empty string a pre-upgrade backend would send.
 */
export function verifiedMeta(status: string): AxisMeta {
  return VERIFIED[status] ?? { label: status || 'NA', ...NEUTRAL, Icon: CircleDashed };
}

// The designer's `.sc-sessions-badge`, including its `w-full`.
//
// That `w-full` is what makes the two status columns read as columns rather than as
// ragged pills: the cell is sized by the widest badge (every badge is nowrap), so
// stretching each badge to 100% of its cell gives the whole column ONE width. Without
// it every badge hugged its own label and the borders zig-zagged down the page —
// which is the spacing difference against the design.
//
// Icons are 14px (`size={14}`) to match the design's `w-3.5 h-3.5` image marks.
const BADGE_BASE =
  'inline-flex w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[length:var(--text-12)] font-medium leading-none whitespace-nowrap';

/**
 * The Verified Status badge is exactly as wide as the menu it opens: `w-52` (208px), the
 * same token as `.sc-sessions-status-dropdown-menu` in the design and as the menu in
 * VerifiedStatusCell. Badge and dropdown share one edge, which is what makes the control
 * read as a single select rather than a pill with a panel loosely under it.
 *
 * FIXED, not `w-full` — and that is the point. `w-full` resolves against the cell, and the
 * table hands leftover width to whichever column will take it, so on a short page (few
 * rows, brief workflow names) the badge ballooned far past the menu. A fixed width takes
 * the column out of that negotiation entirely: the badge is 208px whatever else the page
 * holds, and it comfortably fits the longest verdict ("Exception Requested").
 *
 * Because `cn` is tailwind-merge, this replaces the `w-full` in BADGE_BASE rather than
 * fighting it.
 *
 * Verified axis only. Original Status is read-only with shorter labels and no menu to
 * align to, so it keeps `w-full` — equal width down its own column, as in the design.
 */
const VERIFIED_W = 'w-52';

/**
 * A STEP's stored outcome → the vocabulary the sessions list uses for a run.
 *
 * A step is stored as Passed | Failed | Review | Pending, but those words appear nowhere
 * in the UI: the list says Approved / Rejected / In Review / Not Started. Showing a step
 * as "Passed" on the detail page while the same idea reads "Approved" one screen earlier
 * makes them look like different facts.
 */
const STEP_TO_ORIGINAL: Record<string, string> = {
  Passed: 'Approved',
  Failed: 'Rejected',
  Review: 'InReview',
  Pending: 'NotStarted',
};

/**
 * One step's outcome, drawn with the SAME palette, icon and wording as the sessions
 * list's Original Status — because it is the same information.
 *
 * `w-auto` overrides the shared base's `w-full`: this badge sits in an accordion header
 * beside a chevron, where stretching to the container would push the chevron out. The
 * list needs `w-full` for its column; this does not.
 */
export function StepOutcomeBadge({ status }: { status: string }) {
  const { label, color, fill, Icon } = originalMeta(STEP_TO_ORIGINAL[status] ?? status);
  return (
    <span
      className={cn(BADGE_BASE, 'w-auto')}
      style={{ color, borderColor: color, backgroundColor: fill }}
    >
      <Icon size={14} className="shrink-0" />
      {label}
    </span>
  );
}

/** Read-only pill for the system axis. */
export function OriginalStatusBadge({ status }: { status: string }) {
  const { label, color, fill, Icon } = originalMeta(status);
  return (
    <span className={BADGE_BASE} style={{ color, borderColor: color, backgroundColor: fill }}>
      <Icon size={14} className="shrink-0" />
      {label}
    </span>
  );
}

/**
 * The admin axis as a READ-ONLY pill, for users who cannot change it.
 *
 * Developer and Viewer roles hold no `Session.Reject`, so for them the verdict is
 * information rather than a control. Deliberately not the system-axis badge with a
 * remapped value: this axis's neutral state is labelled "NA", and borrowing
 * "Not Started" from the other axis would state something untrue about the run.
 */
export function VerifiedStatusPill({ status }: { status: string }) {
  const { label, color, fill, Icon } = verifiedMeta(status);
  return (
    <span
      className={cn(BADGE_BASE, VERIFIED_W)}
      style={{ color, borderColor: color, backgroundColor: fill }}
    >
      <Icon size={14} className="shrink-0" />
      {label}
    </span>
  );
}

/**
 * The admin axis, as a button — clicking it opens the verdict menu.
 *
 * Rendered as a real `<button>` (not a styled span) so it is keyboard-reachable and
 * announces itself as actionable: this is the one status in the row a human changes.
 */
export function VerifiedStatusBadge({
  status,
  disabled,
  onClick,
}: {
  status: string;
  disabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const { label, color, fill, Icon } = verifiedMeta(status);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={`Verified status: ${label} — click to change`}
      className={cn(
        BADGE_BASE,
        VERIFIED_W,
        'cursor-pointer select-none transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-50',
      )}
      style={{ color, borderColor: color, backgroundColor: fill }}
    >
      <Icon size={14} className="shrink-0" />
      <span>{label}</span>
      <ChevronDown size={12} className="ml-auto shrink-0 opacity-70" />
    </button>
  );
}

/** One row of the verdict menu, coloured like the badge it will produce. */
export function VerifiedStatusOption({
  value,
  selected,
  onSelect,
}: {
  value: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const { label, color, fill, Icon } = verifiedMeta(value);
  return (
    <button
      type="button"
      onClick={(e) => {
        // The row this menu sits in navigates on click; a verdict must not also open
        // the session.
        e.stopPropagation();
        onSelect();
      }}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[length:var(--text-12)] transition-colors hover:opacity-85"
      style={{
        color,
        backgroundColor: selected ? fill : 'transparent',
        fontWeight: selected ? 600 : 500,
      }}
    >
      <Icon size={14} className="shrink-0" />
      <span>{label}</span>
      {selected && <CheckCircle2 size={12} className="ml-auto shrink-0" />}
    </button>
  );
}
